import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { TrashIcon } from '@heroicons/react/24/solid'
import { AxiosError } from 'axios'
import { FC, useCallback, useEffect } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { ServerValidationError } from '@/api/shared/types'
import { AppInput } from '@/components/shared/input'
import {
  hasPersonServerError,
  parseServerErrorField
} from '@/utils/parseServerErrorField'

import {
  DEFAULT_FULL_NAME_LENGTH,
  DEFAULT_FULL_NAME_LENGTH_MESSAGE_FROM
} from '../../../../requests/model/constants'
import {
  PersonsFormData,
  PersonsFormErrors
} from '../../../../requests/ui/model/schemas'
import { ConfirmDeleteModal } from './ConfirmDeleteModal'
import { useConfirmDelete } from './useConfirmDelete'

type Props = {
  passError?: AxiosError<ServerValidationError> | null
  modalHeader?: string
}

const PassPersonsFormBlock: FC<Props> = ({ passError, modalHeader }) => {
  const {
    control,
    formState: { errors },
    clearErrors,
    setError,
    getValues,
    trigger
  } = useFormContext<PersonsFormData, PersonsFormErrors>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'persons'
  })

  const {
    opened: isDeleteModalOpened,
    requestDelete,
    onClose: handleDeleteCancel,
    onConfirm: handleDeleteConfirm
  } = useConfirmDelete(remove, index => {
    const value = getValues(`persons.${index}.value`)
    return Boolean(value?.trim())
  })

  const isMultipleFields = fields.length > 1

  const applyPersonsServerErrors = useCallback(() => {
    const serverErrors = passError?.response?.data?.errors
    if (!serverErrors?.length) {
      return
    }

    serverErrors.forEach(error => {
      if (!hasPersonServerError(error.field)) {
        return
      }

      const index = parseServerErrorField(error.field)
      const message = error.message
      if (message && index !== null) {
        setError(`persons.${index}.value`, {
          type: 'server',
          message
        })
      }
    })
  }, [passError, setError])

  useEffect(() => {
    applyPersonsServerErrors()
  }, [applyPersonsServerErrors])

  const handleAddPerson = async () => {
    await trigger('persons')
    append({ value: '' })
  }

  return (
    <>
      {fields.map((field, index) => (
        <div key={field.id} className="form-block-cover my-16 p-32 lg:p-16">
          <div className="mb-16 flex  justify-between">
            <p className="form-block-title">
              Пропуск {isMultipleFields && index + 1}
            </p>
            {isMultipleFields && (
              <button
                aria-label="Удалить гостя из списка"
                className="flex items-center justify-center"
                onClick={() => requestDelete(index)}
                type="button"
              >
                <TrashIcon className="h-28 w-28 text-grey-60 xs:h-24 xs:w-24" />
              </button>
            )}
          </div>
          <div className="flex flex-col justify-between gap-x-15">
            <Controller
              name={`persons.${index}.value`}
              control={control}
              defaultValue={field.value}
              render={({ field }) => (
                <AppInput
                  {...field}
                  dataTestId={`pass-person-fio-input-${index + 1}-id`}
                  autoComplete="off"
                  placeholder="ФИО полностью"
                  invalid={Boolean(errors.persons?.[index]?.value)}
                  invalidMessage={errors.persons?.[index]?.value?.message}
                  maxLength={DEFAULT_FULL_NAME_LENGTH}
                  maxLengthMessageFrom={DEFAULT_FULL_NAME_LENGTH_MESSAGE_FROM}
                  className="w-full hyphens-auto break-words border border-grey-20"
                  onChange={event => {
                    clearErrors(field.name)
                    field.onChange(event)
                  }}
                />
              )}
            />
          </div>
        </div>
      ))}

      <button
        className="mb-30 flex items-center gap-x-4"
        onClick={handleAddPerson}
        type="button"
      >
        <PlusCircleIcon className="h-20 w-20 text-bronze-40" />
        <p className="font-bold text-bronze-40 text-16">Добавить ещё</p>
      </button>

      <ConfirmDeleteModal
        opened={isDeleteModalOpened}
        header={modalHeader}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}

export { PassPersonsFormBlock }
